import { FC, type MouseEvent } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import {
  Connect,
  Copy,
  Grid,
  Layers,
  Location,
  Renew,
} from '@carbon/react/icons';
import type { InventoryUnitRotation } from './use-inventory-unit-rotation';
import style from './unit-builder-modal.module.scss';

/**
 * Run a toggle, then drop focus when the click came from a pointer.
 *
 * Carbon paints a tertiary button with a SOLID fill on `:focus` — the same
 * treatment `kind="primary"` uses for the active state — so an "off" toggle kept
 * looking "on" until focus moved, which is why it took a click elsewhere to
 * clear. Blurring here settles it at the source rather than fighting that style.
 *
 * `detail === 0` means the click came from the keyboard (Enter/Space), and those
 * keep focus: a keyboard user has to be able to see where they are.
 *
 * @param {Function} run - The toggle to perform.
 * @return {Function} A click handler.
 */
const toggleThenBlur =
  (run: () => void) =>
  (event: MouseEvent<HTMLButtonElement>): void => {
    run();
    if (event.detail > 0) event.currentTarget.blur();
  };

interface UnitBuilderMapToolbarProps {
  /** Rotate state, owned by the pane so the re-slot can be fired alongside. */
  rotation: InventoryUnitRotation;
  /**
   * The previous rotate's regeneration is still re-slotting the unit across its
   * formations. The rotate button shows that and stays disabled until every
   * formation has reported — rotating again mid-run would race it.
   */
  regenerating?: boolean;
  /**
   * Clone the scoped unit. The mock makes an exact copy at the same coordinates
   * and selects it for editing; disabled while building a brand-new unit.
   */
  onCloneUnit?: () => void;
  /** True while the scoped unit is an unsaved draft, which cannot be cloned. */
  isDraft?: boolean;

  /**
   * Snap dragged vertices to the unit's and its survey areas' own vertices and
   * quarter-grid points. Never to anything the map draws.
   */
  snap: boolean;
  onSnapChange: (value: boolean) => void;
  /** Show the surrounding units around the one being edited. */
  surroundings: boolean;
  onSurroundingsChange: (value: boolean) => void;
  /** Show the landgrid section lines the unit is described against. */
  grid: boolean;
  onGridChange: (value: boolean) => void;
  /** Arm clicking a survey section on the map to add it to the survey areas. */
  surveyPick: boolean;
  onSurveyPickChange: (value: boolean) => void;
  /**
   * Block arming survey picking. An existing (persisted) unit's survey
   * description is read-only, so there is nothing to pick into.
   */
  surveyPickDisabled?: boolean;
}

/**
 * Map View toolbar: rotate, clone, and the three map toggles.
 *
 * The toggles mirror the mock's Snap / Surroundings / Quarter grid trio (sm,
 * icon-only, tooltip below), each filling in (`kind="primary"`) while it is on
 * so its state is visible — all three default to on.
 *
 * @param {UnitBuilderMapToolbarProps} props - Rotation state, clone handler, draft state and the three toggles.
 * @return {JSX.Element} The toolbar.
 */
export const UnitBuilderMapToolbar: FC<UnitBuilderMapToolbarProps> = ({
  rotation,
  regenerating = false,
  onCloneUnit,
  isDraft = false,
  snap,
  onSnapChange,
  surroundings,
  onSurroundingsChange,
  grid,
  onGridChange,
  surveyPick,
  onSurveyPickChange,
  surveyPickDisabled = false,
}) => {
  return (
    <div
      className={style.mapToolbar}
      role="toolbar"
      aria-label="Map view toolbar"
    >
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={rotation.pending || regenerating ? undefined : Renew}
        disabled={rotation.disabled || rotation.pending || regenerating}
        onClick={() => void rotation.toggle()}
      >
        {rotation.pending ? (
          <InlineLoading description="Rotating..." />
        ) : regenerating ? (
          <InlineLoading description="Re-slotting..." />
        ) : rotation.isRotated ? (
          'Restore azimuth'
        ) : (
          'Rotate azimuth 90°'
        )}
      </Button>

      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Copy}
        // A draft has nothing saved to copy from yet, matching the mock.
        disabled={!onCloneUnit || isDraft}
        onClick={onCloneUnit}
      >
        Clone unit
      </Button>

      {/* Pushed right, 4px apart. Each toggle swaps `kind` to primary while on,
          the same way the survey-pick toggle beside them already does — Carbon
          only paints its own selected state for `kind="ghost"`, so the fill is
          what gives these a visible on/off. `aria-pressed` carries the same
          state to assistive tech, since they are toggles. */}
      <div className={style.mapLayerToggles}>
        <Button
          kind={surveyPick ? 'primary' : 'tertiary'}
          size="sm"
          hasIconOnly
          renderIcon={Location}
          iconDescription="Select survey area"
          tooltipPosition="bottom"
          aria-pressed={surveyPick}
          disabled={surveyPickDisabled}
          onClick={toggleThenBlur(() => onSurveyPickChange(!surveyPick))}
        />
        <Button
          kind={snap ? 'primary' : 'tertiary'}
          size="sm"
          hasIconOnly
          renderIcon={Connect}
          iconDescription="Snap"
          tooltipPosition="bottom"
          aria-pressed={snap}
          onClick={toggleThenBlur(() => onSnapChange(!snap))}
        />
        <Button
          kind={surroundings ? 'primary' : 'tertiary'}
          size="sm"
          hasIconOnly
          renderIcon={Layers}
          // Pressed means the surrounding units are SHOWN (the default), so the
          // tooltip names the action the click performs, as the mock's does.
          iconDescription={
            surroundings ? 'Hide Surrounding Units' : 'Show Surrounding Units'
          }
          tooltipPosition="bottom"
          aria-pressed={surroundings}
          onClick={toggleThenBlur(() => onSurroundingsChange(!surroundings))}
        />
        <Button
          kind={grid ? 'primary' : 'tertiary'}
          size="sm"
          hasIconOnly
          renderIcon={Grid}
          iconDescription="Quarter grid"
          tooltipPosition="bottom"
          aria-pressed={grid}
          onClick={toggleThenBlur(() => onGridChange(!grid))}
        />
      </div>
    </div>
  );
};
